/* Admin — Location-wise EOSS Configuration.
 * Admin sets start_date/end_date per location; outside this window the EOSS
 * tab in the DSR form auto-disables.
 */
import { useEffect, useState } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Calendar } from "lucide-react";

const ADMIN_ROLES = new Set(["admin", "spykar_admin", "system_admin"]);

export default function AdminEossConfig() {
    const { user } = useAuth();
    const [configs, setConfigs] = useState([]);
    const [activeMap, setActiveMap] = useState({});
    const [form, setForm] = useState({ location: "", start_date: "", end_date: "", active: true });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const [c, a] = await Promise.all([
                http.get("/eoss-config"),
                http.get("/eoss-config/active"),
            ]);
            setConfigs(c.data); setActiveMap(a.data);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };
    useEffect(() => { if (user) load(); }, [user]);

    const save = async () => {
        if (!form.location || !form.start_date || !form.end_date) {
            toast.error("Fill location + both dates"); return;
        }
        setBusy(true);
        try {
            await http.post("/eoss-config", form);
            toast.success("Saved");
            setForm({ location: "", start_date: "", end_date: "", active: true });
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const remove = async (loc) => {
        if (!window.confirm(`Delete EOSS config for ${loc}?`)) return;
        try { await http.delete(`/eoss-config/${loc}`); toast.success("Deleted"); await load(); }
        catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    if (!user) return null;
    if (!ADMIN_ROLES.has(user.role)) return <div className="p-8 text-center text-muted-foreground">Admin only.</div>;

    return (
        <div className="mx-auto max-w-4xl space-y-5 pb-16" data-testid="admin-eoss-config">
            <div>
                <h1 className="text-[22px] font-semibold tracking-tight">EOSS Configuration</h1>
                <div className="mt-0.5 text-[12px] text-muted-foreground">
                    Enable EOSS tabs & Return Order EOSS block only during the configured window per location.
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2 rounded-md border border-border bg-card p-4 sm:grid-cols-5">
                <input placeholder="Location (e.g. Delhi)" data-testid="ec-location" value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="rounded-md border border-border bg-background px-3 py-2 text-[13px]" />
                <input type="date" data-testid="ec-start" value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="rounded-md border border-border bg-background px-3 py-2 text-[13px]" />
                <input type="date" data-testid="ec-end" value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="rounded-md border border-border bg-background px-3 py-2 text-[13px]" />
                <label className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-[13px]">
                    <input type="checkbox" data-testid="ec-active" checked={form.active}
                        onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                    Active
                </label>
                <button onClick={save} disabled={busy} data-testid="ec-save"
                    className="inline-flex items-center justify-center gap-1 rounded-md bg-teal-600 px-3 py-2 text-[12px] font-semibold text-white">
                    <Plus className="h-3.5 w-3.5" /> Save
                </button>
            </div>

            <div className="overflow-hidden rounded-md border border-border bg-card">
                <table className="w-full text-[12px]">
                    <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Location</th>
                            <th className="px-3 py-2 text-left">Start</th>
                            <th className="px-3 py-2 text-left">End</th>
                            <th className="px-3 py-2 text-center">Active Now</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {configs.map((c) => (
                            <tr key={c.id} className="border-t border-border">
                                <td className="px-3 py-1.5 font-medium">{c.location}</td>
                                <td className="px-3 py-1.5"><Calendar className="mr-1 inline h-3 w-3" />{c.start_date}</td>
                                <td className="px-3 py-1.5"><Calendar className="mr-1 inline h-3 w-3" />{c.end_date}</td>
                                <td className="px-3 py-1.5 text-center">
                                    {activeMap[c.location] ? (
                                        <span className="rounded-sm bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">ACTIVE</span>
                                    ) : (
                                        <span className="rounded-sm bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">OFF</span>
                                    )}
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                    <button onClick={() => remove(c.location)} data-testid={`ec-del-${c.location}`}
                                        className="rounded-sm p-1 text-red-500 hover:bg-red-50">
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {configs.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No EOSS periods configured.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
